import { Button, DisplayInfo } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { useStepState } from '../../../../../components/step/useStepState';
import { SectionTitle } from '../../../../../components/text';
import { TranslationsKeys } from '../../../../../locales/translations';
import { updateAcademicOfferCurrentStep } from '../../api';
import { useAcademicOfferWizard } from '../../context/AcademicOfferWizardContext';

import { ModalityCodesEnum } from '../../../../SectionsMaintainer/enums/modality-codes.enum';
import {
  NonStructuredAcademicOfferStatus,
  NonStructuredAcademicOfferStepEnum,
} from '../../types';
import { useAcademicOfferScheduleData } from './loaders/AcademicOfferScheduleLoader';
/**
 * Se mantiene AcademicOfferScheduleManager  (CRUD tablas + calendario).
 * Se cambió la fuente de datos: ahora usa el endpoint dedicado GET /schedule
 * que trae sections, timeModules, activities y modalityDeliveryMode en 1 sola llamada,
 * en vez del endpoint genérico getAcademicOfferById que requería múltiples fetches.
 */
import { AcademicOfferScheduleManager } from './parts/AcademicOfferScheduleManager';

function AcademicOfferScheduleContent() {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const { nextStep, prevStep } = useStepState();
  const { ceAcademicOfferId, offerStatus } = useAcademicOfferWizard();
  const isClosed = offerStatus === NonStructuredAcademicOfferStatus.Cerrada;
  /**
   * Se usa scheduleData del endpoint dedicado
   * GET /academic-offers/:id/schedule (NonStructuredAcademicOfferScheduleResponse)
   * en vez de 'offer' del endpoint genérico getAcademicOfferById.
   * Esto permite acceder a modalityDeliveryMode directamente sin navegar relaciones.
   */
  const { data: scheduleData } = useAcademicOfferScheduleData();

  /**
   * El check OA ahora usa scheduleData.modalityDeliveryMode
   * directamente del endpoint dedicado, en vez de navegar:
   * offer.academicOfferStudyPlanVersionOffers[0].studyPlanVersionOffer.studyPlanVersion.studyPlan.modality
   */
  const isOnlineAutoatendida = useMemo(() => {
    return scheduleData?.modalityDeliveryMode === ModalityCodesEnum.OA;
  }, [scheduleData]);

  const handleNext = async () => {
    if (!ceAcademicOfferId) {
      return;
    }
    if (isClosed) {
      nextStep();
      return;
    }
    await updateAcademicOfferCurrentStep(
      ceAcademicOfferId,
      NonStructuredAcademicOfferStepEnum.Summary,
    );
    nextStep();
  };

  const handleBack = async () => {
    if (!ceAcademicOfferId) {
      return;
    }
    if (isClosed) {
      prevStep();
      return;
    }
    await updateAcademicOfferCurrentStep(
      ceAcademicOfferId,
      NonStructuredAcademicOfferStepEnum.Sections,
    );
    prevStep();
  };

  return (
    <>
      <SectionTitle text={t('schedule.title')} />
      <p className="pb-4">{t('schedule.description')}</p>

      {isOnlineAutoatendida ? (
        <Row className="py-5">
          <Col
            xs={12}
            className="d-flex justify-content-center align-items-center"
          >
            <DisplayInfo
              title={t('schedule.onlineAutoatendida.title')}
              textBody={t('schedule.onlineAutoatendida.body')}
            />
          </Col>
        </Row>
      ) : (
        <AcademicOfferScheduleManager isReadOnly={isClosed} />
      )}

      <Row>
        <Col xs={12} md={6} lg={4} className="offset-md-6 offset-lg-8">
          <Row>
            <Col>
              <Button
                text={t('wizard.back')}
                outlined
                fullwidth
                onClick={handleBack}
              />
            </Col>
            <Col>
              <Button text={t('wizard.next')} fullwidth onClick={handleNext} />
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
}

export { AcademicOfferScheduleContent };
